import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import loginImage from '../assets/images/login-image.png';
import logo from '../assets/images/logo-1.svg';
import ReactLoading from 'react-loading'; // Import ReactLoading for the spinner

interface RegisterProps {
  updateUserName: (newUserName: string) => void;
}

const Register: React.FC<RegisterProps> = ({ updateUserName }) => {
  const [formData, setFormData] = useState({
    FullName: '',
    email: '',
    PhoneNumber: '',
    CountryCode: '+91',
    Otp: '',
  });

  const [otp, setOtp] = useState(Array(6).fill('')); // State to store OTP digits
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]); // Refs for OTP inputs
  const [otpError, setOtpError] = useState(false);
  const [attempts, setAttempts] = useState(0); // Count OTP attempts
  const [cooldown, setCooldown] = useState(0); // Initialize cooldown state
  const [isOtpValid, setIsOtpValid] = useState(false); // Track OTP validity
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [errors, setErrors] = useState({
    FullName: '',
    PhoneNumber: '',
  });

  const [loading, setLoading] = useState(false); // State to track loading
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [modalType, setModalType] = useState<"privacy" | "terms" | null>(null); const navigate = useNavigate();
  const location = useLocation();

  const { email, isRegisteredUser } = location.state || {};

  // Autofill email from sign-in page
  useEffect(() => {
    if (email) {
      setFormData((prevData) => ({ ...prevData, email }));
    }
  }, [email]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name === "CountryCode") {

      if (!/^\+?\d*$/.test(value)) {
        return;
      }
    }

    setFormData({
      ...formData,
      [name]: value,
    });

    if (name === "PhoneNumber" && !isRegisteredUser) {
      if (value.length !== 10) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          PhoneNumber: "Phone number must be exactly 10 digits long",
        }));
      } else if (!/^\d+$/.test(value)) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          PhoneNumber: "Phone number must contain only digits",
        }));
      } else {
        setErrors((prevErrors) => ({ ...prevErrors, PhoneNumber: "" }));
      }
    }
  };


  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const value = e.target.value;
    setIsOtpValid(false);
    if (/^\d*$/.test(value)) { // Allow only digits
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      const isComplete = newOtp.every(digit => digit.length === 1);
      setIsOtpValid(isComplete);
      // Move to the next input field if value is entered
      if (value && index < otp.length - 1) {
        otpRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      // Move to the previous input field if backspace is pressed
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setHasAttemptedSubmit(true);

    if (!isRegisteredUser && !agreedToTerms) {
      if (!toast.isActive("terms-error")) {
        toast.error('Please agree to the Terms of Service and Privacy Policy', { toastId: "terms-error" });
      }
      return;
    }

    if (otp.join('').length !== 6) {
      if (!toast.isActive("otp-missing")) {
        toast.error('Please enter the OTP', { toastId: "otp-missing" });
      }
      setOtpError(true);
      return;
    }
    if (!isRegisteredUser && (!formData.FullName || !formData.PhoneNumber || !formData.email)) {
      if (!toast.isActive("form-error")) {
        toast.error('Please fill out all fields', { toastId: "form-error" });
      }
      setOtpError(true);
      return;
    }


    if (!isRegisteredUser && formData.PhoneNumber.length !== 10) {
      if (!toast.isActive("phone-error")) {
        toast.info('Phone number must be exactly 10 digits long', { toastId: "phone-error" });
      }
      return;
    }

    setLoading(true); // Set loading state to true when submitting
    setOtpError(false); // Reset OTP error when submitting
    setAttempts(attempts + 1); // Increment OTP attempts

    const FullNo = formData.PhoneNumber ? formData.CountryCode + formData.PhoneNumber : null;

    const payload = {
      email: formData.email,
      userName: isRegisteredUser ? null : formData.FullName,
      phoneNumber: isRegisteredUser ? null : FullNo, // Use FullNo instead of PhoneNumber
      mailOtp: otp.join(''),
    };

    try {
      const response = await fetch('https://pamsapi.vofoxsolutions.com/api/Account/ValidateOtpAsync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const responseData = await response.json();

        const token = responseData.token;
        if (token) {
          localStorage.setItem("token", token);

          const profileResponse = await fetch('https://pamsapi.vofoxsolutions.com/api/User/GetUserProfile', {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          if (profileResponse.ok) {
            const profileData = await profileResponse.json();
            const userName = profileData.userName;

            if (userName) {
              localStorage.setItem('userName', userName);
              updateUserName(userName);

              toast.success('OTP validated successfully!');
              setTimeout(() => {
                navigate('/home');
              }, 1000);
            } else {
              toast.error('User profile not found');
            }
          }
          else {
            const errorData = await profileResponse.json();
            toast.error(`Failed to fetch user profile: ${errorData.message || 'Unknown error'}`);
          }
        } else {
          console.error('Token not received');
          toast.error('Token not received in response');
        }
      } else {
        const errorData = await response.json();
        if (!toast.isActive("otp-error")) {

          toast.error(`OTP validation failed: ${errorData.message || 'Incorrect OTP'}`, { toastId: "otp-error" });
        }
        setOtp(["", "", "", "", "", ""]); // Clear OTP fields
        setOtpError(true);
      }
    } catch (error) {
      console.error('Error during OTP validation:', error);
      toast.error('Something went wrong!');
    } finally {
      setLoading(false); // Set loading state to false after request
    }

  };

  const handleResendOtp = async () => { // Updated function
    if (!formData.email) {
      toast.error('Please provide a valid email!');
      return;
    }

    setAttempts(0); // Reset OTP attempts
    setLoading(true);

    try {
      const response = await fetch('https://pamsapi.vofoxsolutions.com/api/Account/GenerateOtpAsync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: formData.email }),
      });

      if (response.ok) {
        const responseData = await response.json();
        if (!responseData.isRegisteredUser) {
          // toast.success('You are not Registered yet');
        }
        setCooldown(30);
        setTimeout(() => {
          navigate('/register', {
            state: {
              email: formData.email,
              otpSent: true,
              isRegisteredUser: responseData.isRegisteredUser,
            },
          });
        }, 1500);
      } else {
        toast.error('Failed to generate OTP!');
      }
    } catch (error) {
      toast.error('Something went wrong while generating OTP!');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (cooldown > 0) {
      const timer = setInterval(() => {
        setCooldown((prevCooldown) => prevCooldown - 1);
      }, 1000);

      // Cleanup the interval on component unmount
      return () => clearInterval(timer);
    }
  }, [cooldown]);

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSubmit(e);
  };

  const openModal = (type: "privacy" | "terms") => {
    setModalType(type);
  };

  const closeModal = () => {
    setModalType(null);
  };

  const privacyPolicyContent = `
  <h1 class="text-2xl font-bold text-center">GET ME THERE</h1>
  <p class="mt-4">
    Our Privacy Policy explains what personal information we collect, how we use personal information, how personal information is shared, and your privacy rights.
  </p>
  <h2 class="text-xl font-bold mt-6">Privacy Policy</h2>
  <p class="text-sm">Last Updated: 12 January 2025</p>
  <p class="mt-4">
    <strong>GET ME THERE</strong> is an application created to register & locate an address within India and enable you to share your address publicly with the help of a 10-digit code, in extension with your existing PIN code. As a fundamental part of trust, we are clear about how we use your information and protect your rights to privacy.
  </p>
  <p class="mt-4">
    The Privacy Policy describes how <strong>GET ME THERE</strong> and its affiliates (we, us, or <strong>GET ME THERE</strong>) process personal information according to your use of the application.
  </p>
  <h3 class="text-lg font-bold mt-6">Personal information we collect:</h3>
  <ul class="list-disc ml-6 mt-2">
    <li>We collect your personal information when you use the app. This includes your name, email id, phone number, location info, postal address, profile photo, and images from various locations.</li>
    <li><strong>Identify info:</strong> When appropriate, we may ask you to provide identity information for credibility.</li>
    <li><strong>Payment information:</strong> If you avail any paid services, we may ask for your payment info (but we do not store your bank details).</li>
    <li><strong>Information about others:</strong> For shared addresses or public institutions, data may be provided based on a responsible authority's request.</li>
    <li><strong>Biometric information:</strong> We may use data from your photos or other identification documents.</li>
    <li><strong>Other information:</strong> Data provided via forms and surveys to improve the platform.</li>
  </ul>
  <p class="mt-4">
    We automatically collect device information such as GPS location, search history, IP address, and device identification number—even if you haven’t created an account.
  </p>
  <h3 class="text-lg font-bold mt-6">How do we verify your identity?</h3>
  <p class="mt-2">
    We send a 6-digit code to your email, mobile phone, or via WhatsApp. Submitting this code verifies your identity and confirms your agreement with our terms.
  </p>
`;

  // Fancier HTML content for the Terms of Service
  const termsOfServiceContent = `
  <h1 class="text-2xl font-bold text-center">GET ME THERE</h1>
  <p class="mt-4">
    Any form of misinformation that causes damage to our users may result in legal action.
  </p>
  <p class="mt-4">
    <strong>GET ME THERE</strong> is a platform for users to share address and location information, which will be public. By using the platform, you agree to hand over the rights to your data (except personal details such as name, email id, and phone number) if you decide to leave.
  </p>
  <p class="mt-4">
    If a complaint is made against you by any user or member of the public, we may receive or provide related information to address the issue as per legal requirements.
  </p>
  <h3 class="text-lg font-bold mt-6">Sharing data with your consent:</h3>
  <ul class="list-disc ml-6 mt-2">
    <li>We may share your data with other members or with service providers who help us improve our services (e.g., Microsoft, Google, GIS/GPS providers, postal department, courier services, Uber, or taxi services).</li>
  </ul>
  <h3 class="text-lg font-bold mt-6">Disclosure:</h3>
  <ul class="list-disc ml-6 mt-2">
    <li>We may disclose your information to courts, law enforcement, governmental or public authorities, tax authorities, or authorized third parties.</li>
  </ul>
  <h3 class="text-lg font-bold mt-6">Exit policy:</h3>
  <p class="mt-2">
    You have the right to exit the platform at any time. Upon exit, we will remove all your personal information from the system.
  </p>
`;

  return (
    <div className="content-wrapper select-none">
      <main>
        <div className="mx-auto max-w-screen-2xl h-screen flex items-center p-4 md:p-6 2xl:p-10">
          <div className="flex flex-wrap items-center">
            <div className="hidden w-full h-full lg:block lg:w-3/5">
              <div className="px-26 py-17.5 text-center">
                <img
                  className="h-full w-full object-cover"
                  src={loginImage}
                  alt="illustration"
                />
              </div>
            </div>

            <div className="w-full lg:w-2/5">
              <div className="w-full lg:w-3/4 p-4 sm:p-12.5 xl:p-17.5 m-auto">
                <a className="mb-8 inline-block" href="">
                  <img src={logo} alt="Logo" />
                </a>
                <h1 className="mb-9 text-3xl font-semibold text-dark">
                  {isRegisteredUser ? 'Log In to Your Account' : 'Verify Your Account'}
                </h1>


                <form onSubmit={handleFormSubmit}>
                  {!isRegisteredUser && (
                    <>
                      <div className="mb-6">
                        <input
                          type="text"
                          name="FullName"
                          placeholder="Enter your full name"
                          value={formData.FullName}
                          onChange={handleChange}
                          maxLength={32}
                          className="w-full rounded-lg bg-slate-50 py-4 pl-6 pr-10 outline-none focus:border-blue focus-visible:shadow-none"
                          required={!isRegisteredUser}
                        />
                        {errors.FullName && <span className="text-red-500">{errors.FullName}</span>}
                      </div>

                      <div className="mb-6">
                        <div className="flex">
                          <input
                            type="text"
                            name="CountryCode"
                            value={formData.CountryCode}
                            maxLength={5}
                            onChange={handleChange}
                            required={!isRegisteredUser}
                            className="w-[80px] rounded-lg mr-2 bg-slate-50 py-4 pl-4 pr-2 outline-none focus:border-blue focus-visible:shadow-none"
                          />

                          <input
                            type="text"
                            name="PhoneNumber"
                            placeholder="Enter your phone number"
                            value={formData.PhoneNumber}
                            onChange={(e) => {
                              const { value } = e.target;
                              if (/^\d*$/.test(value) || value === "") {
                                handleChange(e);
                              }
                            }}
                            maxLength={10}
                            className="w-full rounded-lg bg-slate-50 py-4 pl-6 pr-10 outline-none focus:border-blue focus-visible:shadow-none"
                            required={!isRegisteredUser}
                          />
                        </div>

                        {errors.PhoneNumber && (
                          <span className="text-red-500 text-sm mt-1 block">{errors.PhoneNumber}</span>
                        )}
                      </div>


                    </>
                  )}

                  <div className="mb-6">
                    <input
                      type="email"
                      name="email"
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full rounded-lg bg-slate-50 py-4 pl-6 pr-10 outline-none focus:border-blue focus-visible:shadow-none"
                      readOnly
                      required
                    />
                  </div>

                  <div className="mb-6">
                    {/* Grid layout for 6 OTP input fields */}
                    <div className="grid grid-cols-6 gap-4 w-full">
                      {otp.map((digit, index) => (
                        <input
                          key={index}
                          ref={ref => otpRefs.current[index] = ref}
                          id={`otp-${index}`}
                          type="text"
                          value={digit}
                          onChange={(e) => handleOtpChange(e, index)}
                          onKeyDown={(e) => handleKeyDown(e, index)}
                          maxLength={1}
                          className={`text-center border ${otpError && otp.join('').length !== 6 ? 'border-red-500' : 'border-whitesmoke'

                            } rounded-lg bg-ghostwhite py-4 outline-none focus:border-blue focus-visible:shadow-none w-full`}
                        />
                      ))}
                    </div>

                    <p className="text-gray-500 mt-2">OTP has been sent to your email</p>
                  </div>
                  {!isRegisteredUser && (
                  <div className="mb-6 flex items-center">
                    <input
                      type="checkbox"
                      id="terms"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      // Increase the size of the checkbox
                      style={{ transform: 'scale(1.2)' }}
                      className="mr-3 mb-5"
                    />
                    <span className="text-gray-700 text-sm font-bold">
                      I have read and agree to GET ME THERE's{' '}
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          openModal("terms");
                        }}
                        className="text-blue-600 cursor-pointer hover:underline"
                      >
                        Terms of Service
                      </span>{' '}
                      and{' '}
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          openModal("privacy");
                        }}
                        className="text-blue-600 cursor-pointer hover:underline"
                      >
                        Privacy Policy
                      </span>
                    </span>
                  </div>

                      )}
                  <div className="mb-4 text-center">
                    <p className="text-gray-500">
                      Didn't receive OTP?&nbsp;
                      <button
                        type="button"
                        onClick={handleResendOtp} // Updated function name
                        className={`text-blue-600 hover:underline ${cooldown > 0 ? 'cursor-not-allowed text-gray-400' : ''}`}
                        disabled={loading || cooldown > 0}
                      >
                        {cooldown > 0 ? `Resend OTP in ${cooldown}s` : 'Resend OTP'}
                      </button>
                    </p>
                  </div>


                  <div>
                    <button
                      type="submit"
                      className="w-full cursor-pointer bg-blue-600 shadow-lg shadow-blue-300/50 rounded-lg border border-primary p-4 font-medium text-white transition hover:bg-blue-500 relative flex justify-center items-center"
                      disabled={loading || attempts >= 5} // Disable the button while loading or after 5 attempts
                    >
                      {loading ? (
                        <ReactLoading type="spin" color="#fff" height={24} width={24} />
                      ) : (
                        'Validate OTP'
                      )}
                    </button>
                  </div>

                  {attempts >= 5 && (
                    <div className="mt-4 text-center">
                      <button
                        type="button"
                        onClick={handleResendOtp} // Updated function name
                        className="w-full cursor-pointer bg-red-600 shadow-lg shadow-red-300/50 rounded-lg border border-primary p-4 font-medium text-white transition hover:bg-red-500"
                      >
                        Resend OTP
                      </button>
                    </div>
                  )}

                </form>
              </div>
            </div>
          </div>
        </div>
      </main>
      {modalType && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-md max-w-xl mx-auto relative">
            <h2 className="text-xl font-bold mb-4">
              {modalType === "privacy" ? "Privacy Policy" : "Terms of Service"}
            </h2>
            <div
              className="max-h-96 overflow-y-auto text-sm text-gray-700"
              // Render the HTML markup using dangerouslySetInnerHTML
              dangerouslySetInnerHTML={{ __html: modalType === "privacy" ? privacyPolicyContent : termsOfServiceContent }}
            />
            <button
              onClick={closeModal}
              className="mt-4 bg-blue-600 text-white py-2 px-4 rounded"
            >
              Close
            </button>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>

  );
};

export default Register;
